import { useMutation } from '@base-app/web2'
import { memo, useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { CREATE_RADIO, UPDATE_RADIO } from '../../../graphql/radios/mutations'
import { RADIO_QUERY } from '../../../graphql/radios/queries'
import type { Radio } from '../../../graphql/radios/types'
import { useRecordById } from '../../../hooks/useRecordById'
import { useToast } from '../../../hooks/useToast'
import { prependToLists } from '../../../lib/cacheLists'
import { isWebUrl } from '../../../services/url-validation'
import { Audio, Button, FormActions } from '../../atoms'
import { FormField, FormLoadState, TextareaField } from '../../molecules'

// Same limit as the API (MAX_UNAVAILABLE_MESSAGE_LENGTH)
const UNAVAILABLE_MESSAGE_MAX_LENGTH = 200

interface RadioFormFieldsProps {
  radio?: Radio
  clientId: string
  appId: string
  onSuccess: () => void
  onClose: () => void
  // Renders every field as its pulsing placeholder while the record loads
  skeleton?: boolean
}

export const RadioFormFields = memo(
  ({ radio, clientId, appId, onSuccess, onClose, skeleton }: RadioFormFieldsProps) => {
    const { t } = useTranslation('admin-mobifull')
    const { showToast } = useToast()
    const isEdit = !!radio
    const [name, setName] = useState(radio?.name ?? '')
    const [streamUrl, setStreamUrl] = useState(radio?.stream_url ?? '')
    const [listeningUrl, setListeningUrl] = useState(radio?.listening_url ?? '')
    const [oldId, setOldId] = useState(radio?.old_id ?? '')
    const [observations, setObservations] = useState(radio?.observations ?? '')
    const [unavailableMessage, setUnavailableMessage] = useState(radio?.unavailable_message ?? '')
    const [errors, setErrors] = useState<{
      name?: string
      streamUrl?: string
      listeningUrl?: string
    }>({})

    const makeOptimistic = useCallback(
      (id: string): Radio => ({
        __typename: 'Radio',
        id,
        client_id: clientId,
        application_id: appId,
        name,
        stream_url: streamUrl || null,
        listening_url: listeningUrl || null,
        old_id: oldId || null,
        observations: observations || null,
        unavailable_message: unavailableMessage.trim() || null,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      }),
      [clientId, appId, name, streamUrl, listeningUrl, oldId, observations, unavailableMessage],
    )

    const [createRadio, { loading: creating }] = useMutation(CREATE_RADIO, {
      optimisticResponse: { createRadio: makeOptimistic(`temp-${Date.now()}`) },
      update(cache, result) {
        const item = result.data?.createRadio
        if (!item) {
          return
        }
        prependToLists(cache, 'radios', item, { clientId, applicationId: appId })
      },
      onError(err) {
        showToast(err.message, 'error')
      },
      onCompleted() {
        onSuccess()
      },
    })

    const [updateRadio, { loading: updating }] = useMutation(UPDATE_RADIO, {
      optimisticResponse: { updateRadio: makeOptimistic(radio?.id ?? '') },
      onError(err) {
        showToast(err.message, 'error')
      },
      onCompleted() {
        onSuccess()
      },
    })

    const loading = creating || updating

    const validate = useCallback(() => {
      const errs: { name?: string; streamUrl?: string; listeningUrl?: string } = {}
      if (!name.trim()) {
        errs.name = t('common.form.nameRequired')
      }
      if (!isWebUrl(streamUrl.trim())) {
        errs.streamUrl = t('common.form.httpsUrlInvalid')
      }
      if (!isWebUrl(listeningUrl.trim())) {
        errs.listeningUrl = t('common.form.httpsUrlInvalid')
      }
      setErrors(errs)
      return Object.keys(errs).length === 0
    }, [name, streamUrl, listeningUrl, t])

    const handleSubmit = useCallback(
      (e: React.FormEvent) => {
        e.preventDefault()
        if (!validate()) {
          return
        }
        const vars = {
          name,
          stream_url: streamUrl || null,
          listening_url: listeningUrl || null,
          old_id: oldId || null,
          observations: observations || null,
          unavailable_message: unavailableMessage.trim() || null,
        }
        if (isEdit) {
          updateRadio({ variables: { id: radio.id, ...vars } })
        } else {
          createRadio({ variables: { client_id: clientId, application_id: appId, ...vars } })
        }
      },
      [
        validate,
        isEdit,
        radio?.id,
        name,
        streamUrl,
        listeningUrl,
        oldId,
        observations,
        unavailableMessage,
        clientId,
        appId,
        updateRadio,
        createRadio,
      ],
    )

    return (
      <form onSubmit={handleSubmit} noValidate aria-busy={skeleton}>
        {skeleton ? (
          <p className="sr-only" role="status">
            {t('common.loading')}
          </p>
        ) : null}
        <FormField
          skeleton={skeleton}
          id="radio-name"
          label={t('radios.form.nameLabel')}
          name="name"
          value={name}
          onChange={e => setName(e.target.value)}
          error={errors.name}
          required
        />
        <FormField
          skeleton={skeleton}
          id="radio-stream-url"
          label={t('radios.form.streamUrlLabel')}
          name="stream_url"
          value={streamUrl}
          onChange={e => setStreamUrl(e.target.value)}
          error={errors.streamUrl}
          placeholder="https://"
        />
        <Audio
          skeleton={skeleton}
          key={streamUrl}
          src={streamUrl}
          label={t('radios.form.audioPreviewLabel')}
        />
        <FormField
          skeleton={skeleton}
          id="radio-listening-url"
          label={t('radios.form.listeningUrlLabel')}
          name="listening_url"
          value={listeningUrl}
          onChange={e => setListeningUrl(e.target.value)}
          error={errors.listeningUrl}
          placeholder="https://"
        />
        <FormField
          skeleton={skeleton}
          id="radio-unavailable-message"
          label={t('radios.form.unavailableMessageLabel')}
          name="unavailable_message"
          value={unavailableMessage}
          onChange={e => setUnavailableMessage(e.target.value)}
          placeholder={t('radios.form.unavailableMessagePlaceholder')}
          hint={t('radios.form.unavailableMessageHint')}
          maxLength={UNAVAILABLE_MESSAGE_MAX_LENGTH}
        />
        <FormField
          skeleton={skeleton}
          id="radio-old-id"
          label={t('radios.form.oldIdLabel')}
          name="old_id"
          value={oldId}
          onChange={e => setOldId(e.target.value)}
        />
        <TextareaField
          skeleton={skeleton}
          id="radio-observations"
          label={t('radios.form.observationsLabel')}
          name="observations"
          value={observations}
          onChange={e => setObservations(e.target.value)}
        />
        <FormActions>
          <Button type="button" variant="secondary" onClick={onClose} disabled={loading}>
            {t('common.actions.cancel')}
          </Button>
          <Button skeleton={skeleton} type="submit" disabled={loading}>
            {loading ? t('radios.form.saving') : t('radios.form.save')}
          </Button>
        </FormActions>
      </form>
    )
  },
)

RadioFormFields.displayName = 'RadioFormFields'

interface RadioFormProps extends Omit<RadioFormFieldsProps, 'radio'> {
  // The record to edit, by id; omit it to create a new one
  id?: string
}

// Loads the record by id (from the cache when the list has it, otherwise from the API)
export const RadioForm = memo(({ id, ...props }: RadioFormProps) => {
  const { record, status } = useRecordById(RADIO_QUERY, id, data => data.radio)
  if (status === 'loading') {
    return <RadioFormFields skeleton {...props} />
  }
  if (status !== 'new' && status !== 'ready') {
    return <FormLoadState status={status} onClose={props.onClose} />
  }
  return <RadioFormFields key={id ?? 'new'} radio={record} {...props} />
})

RadioForm.displayName = 'RadioForm'
